import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import PublicLayout from "@/components/layout/PublicLayout";
import TrackPageView from "@/components/tracking/TrackPageView";
import { toResult } from "@/components/tracking/trackingAdapter";
import {
  Search, CheckCircle2, Truck, ArrowRight, AlertTriangle, RotateCcw,
  Package, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";

const NAVY = "#0A183D";

const STATUS_STYLE = (s) => {
  const k = (s || "").toUpperCase();
  if (k === "DELIVERED" || k === "3PL_DELIVERED")
    return { bg: "bg-emerald-50 border-emerald-300 text-emerald-800", icon: CheckCircle2 };
  if (k === "OUT_FOR_DELIVERY" || k === "3PL_OUT_FOR_DELIVERY")
    return { bg: "bg-teal-50 border-teal-300 text-teal-800", icon: Truck };
  if (k === "IN_TRANSIT" || k === "3PL_IN_TRANSIT" || k === "FM_IN_TRANSIT")
    return { bg: "bg-blue-50 border-blue-300 text-blue-800", icon: ArrowRight };
  if (k === "NDR_RAISED" || k === "3PL_NDR" || k === "DELIVERY_ATTEMPTED")
    return { bg: "bg-amber-50 border-amber-300 text-amber-800", icon: AlertTriangle };
  if (k === "RTO_INITIATED" || k === "LOST")
    return { bg: "bg-red-50 border-red-300 text-red-800", icon: RotateCcw };
  return { bg: "bg-slate-100 border-slate-300 text-slate-700", icon: Package };
};

const fmtType = (t) =>
  (t || "")
    .replace(/^3PL_/, "Courier ")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
};

// Unchanged API — also used by the admin/customer tracking views.
export function TrackingTimeline({ events, embedded = false }) {
  const [expanded, setExpanded] = useState(false);
  if (!events?.length) {
    return (
      <div className={`text-center py-8 text-slate-400 ${embedded ? "" : "border border-dashed border-slate-200 rounded-xl"}`}>
        <Package className="w-8 h-8 mx-auto mb-2 opacity-60" />
        <p className="text-sm">No tracking events yet</p>
      </div>
    );
  }
  const sorted = [...events].sort(
    (a, b) => new Date(b.event_date || 0) - new Date(a.event_date || 0)
  );
  const show = expanded ? sorted : sorted.slice(0, 3);
  return (
    <div data-testid="tracking-timeline" className="relative">
      <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
      <div className="space-y-4">
        {show.map((ev, i) => {
          const isLatest = i === 0;
          return (
            <div key={ev.event_id || i} className="relative pl-9">
              <div
                className={`absolute left-1 top-1.5 w-5 h-5 rounded-full border-2 ${
                  isLatest
                    ? "bg-slate-800 border-slate-800 animate-pulse"
                    : "bg-emerald-500 border-emerald-500"
                }`}
              />
              <div>
                <div className="font-semibold text-slate-800 text-sm">
                  {fmtType(ev.event_type)}
                </div>
                <div className="text-sm text-slate-600">{ev.display_message}</div>
                {ev.display_location && (
                  <div className="text-xs text-slate-500 mt-0.5">📍 {ev.display_location}</div>
                )}
                <div className="text-xs text-slate-400 mt-1">{fmtDate(ev.event_date)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {sorted.length > 3 && (
        <button
          onClick={() => setExpanded((s) => !s)}
          className="mt-3 ml-9 py-1 text-xs text-blue-600 font-semibold flex items-center gap-1"
          data-testid="toggle-timeline-expand"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3.5 h-3.5" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3.5 h-3.5" /> Show all {sorted.length} events
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default function TrackShipment() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("awb") || params.get("tid") || "";
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replayKey, setReplayKey] = useState(0);

  const search = async (q) => {
    const term = (q || "").trim();
    if (!term) return;
    setParams({ awb: term });
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.get(`/v2/tracking/${encodeURIComponent(term)}`);
      const d = r.data;
      if (!d.found && (!d.events || !d.events.length)) {
        setError("No shipment found for this number. Please check it and try again.");
      } else {
        setResult(toResult(d, term));
        setReplayKey((k) => k + 1);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to fetch tracking. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PublicLayout>
      <TrackPageView
        loading={loading}
        error={error}
        result={result}
        onSearch={search}
        initialTerm={initial}
        replayKey={replayKey}
      />
    </PublicLayout>
  );
}
